/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { ChangeEvent } from "react";
import { FastField, Field, FormikProps, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Autocomplete from "@mui/material/Autocomplete";
import CustomizedSwitch from "../formElements/Switch";
import CustomizedRadio from "../formElements/RadioButton";
import CustomizedCheckbox from "../formElements/Checkbox";
import TimeInput from "../formElements/TimeInput";

export type Option = { label: string; value: string | number | boolean };

export interface DynamicFieldProps {
  fieldType:
    | "text"
    | "number"
    | "select"
    | "checkbox"
    | "switch"
    | "autocomplete"
    | "timeinput"
    | "radio";
  label: string;
  name: string;
  options?: Option[];
  fullWidth?: boolean;
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  validationSchema?:
    | Yup.StringSchema
    | Yup.NumberSchema
    | Yup.BooleanSchema
    | Yup.ArraySchema<any, unknown, unknown, any>
    | Yup.ObjectSchema<any, unknown, unknown, any>;
}

const useStyles = makeStyles(() => ({
  error: {
    "& .MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
      {
        borderColor: "red",
      },
    "& p": {
      color: "red",
    },
  },
  textInput: {
    "& .MuiFormLabel-root": {
      textTransform: "unset",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      right: "22px",
      // bottom: 0,
    },
  },
  autocomplete: {
    "& .MuiFormLabel-root": {
      textTransform: "unset",
    },
  },
}));

export const DynamicField: React.FC<DynamicFieldProps> = ({
  fieldType,
  label,
  name,
  options,
  ...rest
}) => {
  const classes = useStyles();
  const { errors, values, setFieldValue } = useFormikContext();
  const errorMessage = getIn(errors, name);

  const handleAddOptions = (
    e: ChangeEvent<HTMLInputElement>,
    op: Option,
    name: string
  ) => {
    // @ts-ignore
    const optionValues = values[name];
    if (e.currentTarget.checked) {
      setFieldValue(
        `['${name}']`,
        optionValues !== undefined ? [...optionValues, op.label] : [op.label]
      );
    } else {
      setFieldValue(
        `['${name}']`,
        optionValues.filter(
          (x: string) => x.toLowerCase() !== op.label.toLowerCase()
        )
      );
    }
  };

  switch (fieldType) {
    case "text":
    case "number":
      return (
        <FastField
          fullWidth
          as={TextField}
          type={fieldType === "number" ? "number" : "text"}
          name={`['${name}']`}
          // @ts-ignore
          value={values[name]}
          label={label}
          variant="outlined"
          size="small"
          {...rest}
          helperText={errorMessage}
          className={errorMessage ? classes.error : classes.textInput}
        />
      );
    case "autocomplete": {
      return options && options.length ? (
        <Field name={name}>
          {() => {
            return (
              <Autocomplete
                multiple
                fullWidth
                size="small"
                id={name}
                // @ts-ignore
                value={values[name] || []}
                options={options || []}
                getOptionLabel={(option) => option.label}
                defaultValue={[]}
                filterSelectedOptions
                onChange={(e: ChangeEvent<object>, newOption: Option[]) =>
                  setFieldValue(`['${name}']`, newOption)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={label}
                    placeholder="Select option"
                  />
                )}
                className={classes.autocomplete as string}
              />
            );
          }}
        </Field>
      ) : null;
    }
    case "select": {
      return options && options.length ? (
        <Field name={name}>
          {() => {
            return (
              <Autocomplete
                fullWidth
                size="small"
                id={name}
                // @ts-ignore
                value={values[name] || null}
                options={options || []}
                getOptionLabel={(option) => option.label}
                defaultValue={""}
                filterSelectedOptions
                disableClearable={true}
                onChange={(
                  event: ChangeEvent<object>,
                  value: Option | null
                ) => {
                  setFieldValue(`['${name}']`, {
                    label: value?.label || "",
                    value: value?.value || "",
                  });
                }}
                renderInput={({inputProps, ...params}) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={label}
                    placeholder="Select option"
                    inputProps={{...inputProps, readOnly: true}}
                  />
                )}
                className={classes.autocomplete}
              />
            );
          }}
        </Field>
      ) : null;
    }
    case "checkbox": {
      return options && options.length ? (
        <>
          <InputLabel shrink style={{ marginBottom: 5 }}>
            {label}
          </InputLabel>
          {options.map((option, idx) => (
            <FormControlLabel
              key={idx}
              control={
                <CustomizedCheckbox
                  // @ts-ignore
                  key={`${idx}_${values[name].length}`}
                  checked={
                    // @ts-ignore
                    Array.isArray(values[name]) &&
                    // @ts-ignore
                    values[name].find((e) => e === option.label) !== undefined
                  }
                  onChange={(e) => handleAddOptions(e, option, name)}
                  name={`['${name}']`}
                />
              }
              label={option.label}
            />
          ))}
        </>
      ) : null;
    }
    case "switch": {
      return options && options.length ? (
        <>
          <InputLabel shrink style={{ marginBottom: 5 }}>
            {label}
          </InputLabel>
          <Box marginLeft={2}>
            {options.map((option, idx) => (
              <FormControlLabel
                key={idx}
                style={{ marginRight: "3rem" }}
                control={
                  <CustomizedSwitch
                    // @ts-ignore
                    key={`${idx}_${values[name].length}`}
                    checked={
                      // @ts-ignore
                      Array.isArray(values[name]) &&
                      // @ts-ignore
                      values[name].find((e) => e === option.label) !== undefined
                    }
                    onChange={(e) => handleAddOptions(e, option, name)}
                    name={`['${name}']`}
                  />
                }
                label={option.label}
              />
            ))}
          </Box>
        </>
      ) : null;
    }
    case "radio": {
      const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFieldValue(`['${name}']`, e.target.value);
      };
      return options && options.length ? (
        <FastField
          name={`['${name}']`}
          as={(props: FormikProps<Option>) => (
            <FormControl fullWidth>
              <InputLabel shrink>{label}</InputLabel>
              <RadioGroup
                {...props}
                style={{ paddingTop: "1rem" }}
                onChange={handleOnChange}
              >
                {options &&
                  options.map((option, idx) => (
                    <CustomizedRadio
                      value={option.value as string}
                      label={option.label}
                      key={idx}
                    />
                  ))}
              </RadioGroup>
            </FormControl>
          )}
        />
      ) : null;
    }
    default:
      return null;
  }
};
